// src/components/ui/date-window-filter.tsx
// IR-1384 (Sean, 2026-08-12): "the Added button only contains 3 options and
// doesn't allow a search on a custom range". The range had in fact shipped
// (IR-1147 reopen) — but as a SEPARATE pill labelled "Created", over the very
// same date — so an operator reading "Added" reasonably concluded a custom
// range was impossible. This control carries both in one place: the quick
// windows and a calendar range, mutually exclusive so they can never
// silently intersect.
import { cn } from "@/lib/cn";
import { format, parseISO } from "date-fns";
import { ChevronDown } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { RangeCalendar } from "./range-calendar";

/** yyyy-MM-dd endpoints — the contract the OPS date filters already speak. */
export interface IsoRange {
  from?: string | undefined;
  to?: string | undefined;
}

export interface DateWindowOption {
  /** Token sent to the backend (e.g. "7d"). */
  value: string;
  label: string;
}

/**
 * Exactly one of these is ever populated: a rolling window token, or an
 * explicit calendar range. Both empty = no date filter.
 */
export interface DateWindowValue {
  within: string;
  range: IsoRange;
}

export const EMPTY_DATE_WINDOW: DateWindowValue = { within: "", range: {} };

const ISO = "yyyy-MM-dd";

export function isDateWindowActive(value: DateWindowValue): boolean {
  return Boolean(value.within || value.range.from || value.range.to);
}

/**
 * What the trigger shows once a filter is on — the window's own label, or the
 * range endpoints. Null when nothing is selected (trigger falls back to the
 * plain label).
 */
export function dateWindowSummary(
  value: DateWindowValue,
  options: DateWindowOption[],
): string | null {
  if (value.within) {
    return options.find((o) => o.value === value.within)?.label ?? value.within;
  }
  const { from, to } = value.range;
  if (from || to) return `${from ?? "…"} – ${to ?? "…"}`;
  return null;
}

function toDraft(v: IsoRange): DateRange | undefined {
  if (!v.from && !v.to) return undefined;
  return {
    from: v.from ? parseISO(v.from) : undefined,
    to: v.to ? parseISO(v.to) : undefined,
  };
}

function toIso(r: DateRange | undefined): IsoRange {
  if (!r?.from) return {};
  return { from: format(r.from, ISO), to: r.to ? format(r.to, ISO) : format(r.from, ISO) };
}

interface Props {
  label: string;
  options: DateWindowOption[];
  value: DateWindowValue;
  onChange: (next: DateWindowValue) => void;
}

export function DateWindowFilter({ label, options, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(toDraft(value.range));
  const [alignRight, setAlignRight] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The panel is wider than the pill that opens it, and "Added" sits mid-row —
  // left-anchored it can run off the right edge of a narrow window, taking the
  // Apply button with it. Measure once per open and flip the anchor.
  useLayoutEffect(() => {
    if (!open) {
      setAlignRight(false);
      return;
    }
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.right > window.innerWidth - 8) setAlignRight(true);
  }, [open]);

  // Re-seed the calendar from the applied value whenever the panel is closed,
  // so an abandoned draft never survives into the next open.
  useEffect(() => {
    if (!open) setDraft(toDraft(value.range));
  }, [value.range, open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const summary = dateWindowSummary(value, options);

  // Picking a quick window clears any calendar range (and vice versa on
  // Apply) — two date filters over one column would AND together and read as
  // "the filter is broken".
  const pickWindow = (token: string) => {
    onChange(value.within === token ? EMPTY_DATE_WINDOW : { within: token, range: {} });
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs",
          "border-[var(--iris-line-2)] bg-[var(--iris-bg-3)] text-[var(--iris-fg-2)]",
          "outline-none hover:bg-[var(--iris-bg-4)]",
          open && "bg-[var(--iris-bg-4)]",
        )}
      >
        <span>{label}</span>
        {summary && (
          <span className="rounded-full bg-[var(--iris-fg-1)] px-1.5 text-[10px] font-semibold text-[var(--iris-bg-0)]">
            {summary}
          </span>
        )}
        <ChevronDown size={12} className="text-[var(--iris-fg-4)]" />
      </button>

      {open && (
        <div
          ref={panelRef}
          className={cn(
            "absolute z-20 mt-1 flex rounded-md border border-[var(--iris-line-2)]",
            "bg-[var(--iris-bg-1)] p-2 shadow-lg",
            alignRight ? "right-0" : "left-0",
          )}
        >
          {/* Quick windows sit alongside the calendar rather than above it —
              same side-by-side shape as the IRIS search date filter, and it
              keeps the panel short enough that Apply stays on screen. */}
          <div className="flex w-28 shrink-0 flex-col gap-0.5 border-r border-[var(--iris-line-2)] pr-2">
            <div className="px-1 pb-1 text-[10px] uppercase tracking-wide text-[var(--iris-fg-4)]">
              Quick
            </div>
            {options.map((opt) => {
              const checked = value.within === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => pickWindow(opt.value)}
                  className={cn(
                    "flex items-center gap-2 rounded px-2 py-1 text-left text-xs",
                    checked
                      ? "bg-[var(--iris-bg-4)] text-[var(--iris-fg-1)]"
                      : "text-[var(--iris-fg-2)] hover:bg-[var(--iris-bg-3)]",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "flex h-3.5 w-3.5 items-center justify-center rounded border text-[10px] leading-none",
                      checked
                        ? "border-[var(--iris-neon-green)] bg-[var(--iris-neon-green)] text-[var(--iris-bg-0)]"
                        : "border-[var(--iris-line-2)]",
                    )}
                  >
                    {checked ? "✓" : ""}
                  </span>
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>

          <div className="pl-2">
            <div className="px-1 pb-1 text-[10px] uppercase tracking-wide text-[var(--iris-fg-4)]">
              Custom range
            </div>
            <RangeCalendar draft={draft} onSelect={setDraft} />
            <div className="mt-2 flex items-center justify-between border-t border-[var(--iris-line-2)] pt-2">
              <button
                type="button"
                className="text-xs text-[var(--iris-fg-3)] hover:text-[var(--iris-fg-1)]"
                onClick={() => {
                  setDraft(undefined);
                  onChange(EMPTY_DATE_WINDOW);
                }}
              >
                Clear
              </button>
              <div className="flex gap-2">
                <button type="button" className="iris-btn text-xs" onClick={() => setOpen(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="iris-btn iris-btn--primary text-xs"
                  disabled={!draft?.from}
                  onClick={() => {
                    onChange({ within: "", range: toIso(draft) });
                    setOpen(false);
                  }}
                  title={draft?.from ? undefined : "Pick a start date first"}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
